import { Calendar, CheckCircle, ChevronLeft, ChevronRight, Circle, Clock, Plus, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import ROUTES from '@/ROUTES'

import { IEvent } from '@/MODELS/event.model'

import { api } from '@/services/api.service'

import { DataView } from '@/components/data-view/DataView'
import Loading from '@/components/Loading'
import Modal from '@/components/Modal'
import { SectionHeader } from '@/components/SectionHeader'
import AddEventForm from '@/forms/EventForm'

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

export default function SchedulesCalendarPage() {
    const navigate = useNavigate()
    const [currentDate, setCurrentDate] = useState(new Date())
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [openForm, setOpenForm] = useState<boolean>(false)
    const calendarData = useMemo(() => {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()

        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)

        const startDate = new Date(firstDay)
        startDate.setDate(startDate.getDate() - firstDay.getDay())

        const endDate = new Date(lastDay)
        endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()))

        const days = []
        const currentDay = new Date(startDate)

        while (currentDay <= endDate) {
            days.push(new Date(currentDay))
            currentDay.setDate(currentDay.getDate() + 1)
        }

        return {
            days,
            firstDay,
            lastDay,
            monthName: MONTHS[month],
            year,
        }
    }, [currentDate])

    const handlePrevMonth = () => {
        setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
    }

    const handleNextMonth = () => {
        setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
    }

    const handleDayClick = (date: Date) => {
        setSelectedDate(date)
        setOpenForm(true)
    }

    async function handleToggleEvent(eventId: string, checked: boolean, get: () => void) {
        try {
            const response = await api(`${ROUTES.app.event}`, {
                _id: eventId,
                checked: checked,
            })

            get()
        } catch (error) {
            console.error('Error updating event:', error)
        }
    }

    const handleAddEventSuccess = (refreshData: () => void) => {
        setSelectedDate(null)
        setOpenForm(false)
        refreshData()
    }

    return (
        <DataView<IEvent> endpoint={ROUTES.app.event}>
            {({ data, isLoading, get }) => {
                const today = new Date()

                return (
                    <div className='min-h-screen'>
                        <SectionHeader title='My Schedule' subtitle='Manage your appointments and events' />

                        <div className='max-w-md px-3 py-6 mx-auto sm:max-w-2xl lg:max-w-7xl sm:px-6 lg:px-8'>
                            {isLoading ? (
                                <div className='flex items-center justify-center py-16'>
                                    <Loading mode='block' isLoading={isLoading} text='Loading schedule...' />
                                </div>
                            ) : (
                                <div className='h-full border shadow-lg bg-white/80 backdrop-blur-sm border-gray-200/50'>
                                    <div className='p-4 border-b sm:p-6 bg-gradient-to-br from-white/90 to-gray-50/60 border-gray-100/50'>
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center space-x-3'>
                                                <div className='flex items-center justify-center w-10 h-10 shadow-md sm:w-12 sm:h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl'>
                                                    <Calendar className='w-5 h-5 text-white sm:w-6 sm:h-6' />
                                                </div>
                                                <div>
                                                    <h2 className='text-lg font-bold text-gray-900 sm:text-xl'>
                                                        {calendarData.monthName} {calendarData.year}
                                                    </h2>
                                                    <p className='text-xs text-gray-500 sm:text-sm'>
                                                        Tap any day to add an event
                                                    </p>
                                                </div>
                                            </div>

                                            <div className='flex items-center space-x-1'>
                                                <button
                                                    onClick={handlePrevMonth}
                                                    className='p-2.5 sm:p-3 bg-white/70 border border-gray-200/60 text-gray-600 rounded-xl hover:bg-white hover:text-brand-600 active:scale-95 transition-all duration-200 touch-manipulation'
                                                >
                                                    <ChevronLeft className='w-5 h-5' />
                                                </button>
                                                <button
                                                    onClick={handleNextMonth}
                                                    className='p-2.5 sm:p-3 bg-white/70 border border-gray-200/60 text-gray-600 rounded-xl hover:bg-white hover:text-brand-600 active:scale-95 transition-all duration-200 touch-manipulation'
                                                >
                                                    <ChevronRight className='w-5 h-5' />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='p-3 sm:p-6'>
                                        <div className='grid grid-cols-7 gap-1 mb-3'>
                                            {DAYS_OF_WEEK.map((day) => (
                                                <div key={day} className='py-2 text-center'>
                                                    <span className='text-xs font-semibold tracking-wide text-gray-500 uppercase'>
                                                        {day.slice(0, 3)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className='grid grid-cols-7 gap-1 sm:gap-2'>
                                            {calendarData.days.map((date, index) => (
                                                <CalendarDay
                                                    key={index}
                                                    date={date}
                                                    isCurrentMonth={
                                                        date.getMonth() === calendarData.firstDay.getMonth()
                                                    }
                                                    isToday={date.toDateString() === today.toDateString()}
                                                    events={data}
                                                    onClick={() => handleDayClick(date)}
                                                    onToggleEvent={(eventId, checked) =>
                                                        handleToggleEvent(eventId, checked, get)
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <Modal open={openForm} onClose={() => setOpenForm(false)}>
                                <AddEventForm
                                    selectedDate={selectedDate}
                                    onCancel={() => setSelectedDate(null)}
                                    onSuccess={() => handleAddEventSuccess(get)}
                                />
                            </Modal>
                        </div>
                    </div>
                )
            }}
        </DataView>
    )
}

function CalendarDay({
    date,
    isCurrentMonth,
    isToday,
    events,
    onClick,
    onToggleEvent,
}: {
    date: Date
    isCurrentMonth: boolean
    isToday: boolean
    events: IEvent[]
    onClick: () => void
    onToggleEvent: (eventId: string, checked: boolean) => void
}) {
    const dayEvents = events.filter((event) => {
        const eventDate = new Date(event.date)
        return eventDate.toDateString() === date.toDateString()
    })

    const hasEvents = dayEvents.length > 0
    const completedEvents = dayEvents.filter((event) => event.checked).length
    const pendingEvents = dayEvents.length - completedEvents

    return (
        <div
            className={`group relative min-h-[60px] sm:min-h-[70px] p-3 sm:p-4 cursor-pointer transition-all duration-200 rounded-xl border touch-manipulation active:scale-95 flex flex-col items-center justify-center
                ${
                    !isCurrentMonth
                        ? 'bg-gray-50/60 border-gray-200/40 text-gray-400 active:bg-gray-100/60'
                        : 'bg-white/70 border-gray-100/60 hover:bg-white/90 hover:border-gray-200/60 active:bg-white'
                } 
                ${isToday ? 'bg-gradient-to-br from-brand-50/90 to-brand-100/70 border-brand-200/60 shadow-sm' : ''}
            `}
            onClick={onClick}
        >
            <div className='relative z-10 flex flex-col items-center justify-center space-y-2'>
                <div className='flex flex-col items-center'>
                    <span
                        className={`text-base sm:text-lg font-bold ${
                            isToday ? 'text-brand-700' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                        }`}
                    >
                        {date.getDate()}
                    </span>

                    {isToday && (
                        <div className='w-2 h-2 rounded-full shadow-sm bg-gradient-to-r from-brand-400 to-brand-500'></div>
                    )}
                </div>

                {hasEvents && (
                    <div className='flex items-center justify-center space-x-1'>
                        {pendingEvents > 0 && (
                            <div className='relative'>
                                {pendingEvents > 0 && (
                                    <div className='absolute flex items-center justify-center w-3 h-3 rounded-full -top-1 -right-1'>
                                        <Clock className='w-5 h-5 text-gray-600' />
                                    </div>
                                )}
                            </div>
                        )}

                        {completedEvents > 0 && (
                            <div className='relative'>
                                <div className='w-2 h-2 bg-gray-100 rounded-full'></div>
                                {completedEvents > 1 && (
                                    <div className='absolute flex items-center justify-center w-3 h-3 rounded-full -top-1 -right-1'>
                                        <CheckCircle className='w-5 h-5 text-green-600' />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
